/**
 * Watch folder scanner using the File System Access API.
 *
 * Lets the pilot pick a directory ONCE (e.g. /sdcard/DJI/DJI Fly/) and then
 * periodically rescans it for new log files (.csv/.txt/.dat). New files are
 * parsed via djiLogParser and added to the offline queue. Seen files are
 * tracked in IndexedDB so we don't re-process them.
 *
 * Supported in Chrome/Edge on Android (covers DJI RC, RC 2, RC Pro, RC Plus,
 * DJI Smart Controller Enterprise - all Chromium-based Android). Not
 * supported on iOS Safari — the app falls back to manual import on iOS.
 */

const WATCH_DB = 'flightsync-watch';
const STORE_HANDLE = 'handles';
const STORE_SEEN = 'seen-files';

function openWatchDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(WATCH_DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_HANDLE)) db.createObjectStore(STORE_HANDLE);
      if (!db.objectStoreNames.contains(STORE_SEEN)) db.createObjectStore(STORE_SEEN);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbGet(store, key) {
  const db = await openWatchDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(store, 'readonly').objectStore(store).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbSet(store, key, value) {
  const db = await openWatchDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(store, 'readwrite').objectStore(store).put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

async function dbDel(store, key) {
  const db = await openWatchDb();
  return new Promise((resolve, reject) => {
    const req = db.transaction(store, 'readwrite').objectStore(store).delete(key);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

export function watchFolderSupported() {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

export async function pickWatchFolder() {
  if (!watchFolderSupported()) {
    throw new Error('File System Access API nao suportada neste navegador (use Android Chrome/Edge)');
  }
  const handle = await window.showDirectoryPicker({
    mode: 'read',
    // Omit startIn so browser remembers last location; on first pick it
    // opens default filesystem view (works well on Android /sdcard).
  });
  await dbSet(STORE_HANDLE, 'root', handle);
  // Reset seen list when folder changes
  const db = await openWatchDb();
  await new Promise((resolve, reject) => {
    const req = db.transaction(STORE_SEEN, 'readwrite').objectStore(STORE_SEEN).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
  return handle.name;
}

export async function getWatchHandle() {
  const handle = await dbGet(STORE_HANDLE, 'root');
  if (!handle) return null;
  return handle;
}

export async function clearWatchFolder() {
  await dbDel(STORE_HANDLE, 'root');
  const db = await openWatchDb();
  await new Promise((resolve) => {
    const req = db.transaction(STORE_SEEN, 'readwrite').objectStore(STORE_SEEN).clear();
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
  });
}

async function ensurePermission(handle) {
  // Chrome returns 'granted' | 'prompt' | 'denied' from queryPermission
  const opts = { mode: 'read' };
  const state = await handle.queryPermission?.(opts);
  if (state === 'granted') return true;
  const req = await handle.requestPermission?.(opts);
  return req === 'granted';
}

const LOG_EXT_RE = /\.(csv|txt|dat)$/i;

async function* walkDirectory(dirHandle, depth = 0, maxDepth = 4) {
  for await (const [name, h] of dirHandle.entries()) {
    if (h.kind === 'file') {
      if (LOG_EXT_RE.test(name)) yield h;
    } else if (h.kind === 'directory' && depth < maxDepth) {
      // Skip hidden folders only (dot-prefixed). DJI Fly logs often live under
      // /Android/data/dji.go.v5/ so we MUST NOT skip 'Android' directories.
      if (name.startsWith('.')) continue;
      yield* walkDirectory(h, depth + 1, maxDepth);
    }
  }
}

/**
 * Scan the watched folder for new log files.
 * Returns array of File objects that were NOT seen before.
 */
export async function scanForNewFiles() {
  const handle = await getWatchHandle();
  if (!handle) return [];
  if (!(await ensurePermission(handle))) return [];

  const newFiles = [];
  for await (const fileHandle of walkDirectory(handle)) {
    try {
      const file = await fileHandle.getFile();
      // Build a stable identity: name + size + lastModified
      const id = `${file.name}:${file.size}:${file.lastModified}`;
      const seen = await dbGet(STORE_SEEN, id);
      if (seen) continue;
      await dbSet(STORE_SEEN, id, { name: file.name, size: file.size, seen_at: Date.now() });
      newFiles.push(file);
    } catch {
      // permission revoked or file moved — skip
    }
  }
  return newFiles;
}
